import { EyeIcon, EyeHideIcon } from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import { useState } from 'react';

export interface PasswordFieldProps {
  /**
   * Sets the `autocomplete` attribute. Use `'new-password'` on Sign Up so
   * password managers offer to generate a credential instead of autofilling
   * an existing one.
   */
  autocomplete?: 'current-password' | 'new-password';

  /** If true, focus this field on mount. */
  autoFocus?: boolean;

  /** Error message shown when the value violates `minLength` or other constraints. */
  errorMessage?: string;

  /** Optional helper text shown below the field (e.g., requirements). */
  helperText?: string;

  /** Field label. */
  label?: string;

  /** Minimum length — enforced client-side. */
  minLength?: number;

  /** Form field name. */
  name?: string;

  /** Required-validation message. */
  requiredMessage?: string;
}

/**
 * Password input with a visibility toggle. Wraps Form.TextField so it integrates
 * with reefer-hook-form's validation/state.
 */
export const PasswordField = ({
  autocomplete = 'current-password',
  autoFocus,
  helperText,
  label = 'Password',
  minLength,
  name = 'password',
  errorMessage,
  requiredMessage = 'Please enter your password.',
}: PasswordFieldProps) => {
  const [visible, setVisible] = useState(false);

  return (
    <Form.TextField
      autoFocus={autoFocus}
      autocomplete={autocomplete}
      disableMobileInputStyling
      endIcon={
        <button
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          data-testid="password-visibility-toggle"
          onClick={() => setVisible((v) => !v)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
          type="button"
        >
          {visible ? <EyeHideIcon size="sm" /> : <EyeIcon size="sm" />}
        </button>
      }
      errorMessage={errorMessage}
      errorOnBlur
      helperText={helperText}
      label={label}
      mb={24}
      minLength={minLength}
      name={name}
      required
      requiredLabel
      requiredMessage={requiredMessage}
      type={visible ? 'text' : 'password'}
    />
  );
};
